import React from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import { useLowStockItems, useLeadTimeAlertCounts } from '@hooks/api/warehouse';
import { WarehouseProvider, useWarehouseContext } from '@components/layout/WarehouseContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ConstructionIcon from '@mui/icons-material/Construction';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CategoryIcon from '@mui/icons-material/Category';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { SidebarNavButton } from '@components/layout/SidebarNavButton';

interface WarehouseLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  id: string;
  name: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  disabled?: boolean;
}

const WarehouseLayoutInner: React.FC<WarehouseLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { warehouses, selectedWarehouse, setSelectedWarehouse, isLoading: warehousesLoading } = useWarehouseContext();
  const [isSelectorOpen, setIsSelectorOpen] = React.useState(false);
  const selectorRef = React.useRef<HTMLDivElement>(null);

  const hasNoWarehouses = !warehousesLoading && warehouses.length === 0;

  // Alerts badge reflects the Alerts page, which shows low stock across ALL
  // warehouses — so the count is global (matches the primary sidebar badge).
  const { data: allLowStockItems = [] } = useLowStockItems();
  const lowStockCount = allLowStockItems.length;

  // Get lead time alert count for badge
  const { data: leadTimeAlertCounts } = useLeadTimeAlertCounts();
  const totalAlertCount = lowStockCount + (leadTimeAlertCounts?.total || 0);

  // Close selector on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Redirect to /warehouse/manage when no warehouses exist (after all hooks)
  if (hasNoWarehouses && !location.pathname.startsWith('/warehouse/manage')) {
    return <Redirect to="/warehouse/manage" />;
  }

  const navItems: NavItem[] = [
    {
      id: 'alerts',
      name: 'Alerts',
      path: '/warehouse/alerts',
      icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
      badge: totalAlertCount > 0 ? totalAlertCount : undefined,
    },
    {
      id: 'overview',
      name: 'Stock Overview',
      path: '/warehouse',
      icon: <DashboardIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'materials',
      name: 'Material Catalog',
      path: '/warehouse/materials',
      icon: <Inventory2Icon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'equipment',
      name: 'Equipment Assets',
      path: '/warehouse/equipment',
      icon: <ConstructionIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'transactions',
      name: 'Transactions',
      path: '/warehouse/transactions',
      icon: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'categories',
      name: 'Categories',
      path: '/warehouse/categories',
      icon: <CategoryIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'reports',
      name: 'Reports',
      path: '/warehouse/reports/valuation',
      icon: <AssessmentIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'manage',
      name: 'Warehouses',
      path: '/warehouse/manage',
      icon: <WarehouseIcon sx={{ fontSize: 18 }} />,
    },
  ];

  // When no warehouses exist, only show the "Warehouses" (manage) nav item
  const visibleNavItems = hasNoWarehouses
    ? navItems.filter((item) => item.id === 'manage')
    : navItems;

  // Determine active item based on current route
  const getActiveItemId = () => {
    const path = location.pathname;
    if (path === '/warehouse') return 'overview';
    if (path.startsWith('/warehouse/materials')) return 'materials';
    if (path.startsWith('/warehouse/equipment')) return 'equipment';
    if (path.startsWith('/warehouse/transactions')) return 'transactions';
    if (path.startsWith('/warehouse/alerts')) return 'alerts';
    if (path.startsWith('/warehouse/categories')) return 'categories';
    if (path.startsWith('/warehouse/reports')) return 'reports';
    if (path.startsWith('/warehouse/manage')) return 'manage';
    return 'overview';
  };

  const activeItemId = getActiveItemId();

  const handleSelectWarehouse = (wh: typeof warehouses[0]) => {
    setSelectedWarehouse(wh);
    setIsSelectorOpen(false);
  };

  return (
    <>
      {/* Secondary Sidebar */}
      <div className="fixed left-52 top-0 self-stretch flex bg-[#43378F] dark:bg-gray-800 min-w-52 max-w-52 h-screen flex-col items-start overflow-y-auto transition-colors duration-200 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.4)_rgba(139,92,246,0.2)] dark:[scrollbar-color:rgba(255,255,255,0.3)_rgba(31,41,55,0.3)]">
        {/* Header */}
        <div className="flex items-center gap-2 px-3.5 py-2.5 w-full border-b border-white/10">
          <WarehouseIcon sx={{ fontSize: 20 }} className="text-white" />
          <span className="text-white font-semibold text-sm">Warehouse</span>
        </div>

        {/* Warehouse Selector & Info Card */}
        {warehouses.length > 0 && (
          <div className="w-full px-3 py-2 border-b border-white/10" ref={selectorRef}>
            {/* Selector Button */}
            <button type="button"
              onClick={() => setIsSelectorOpen(!isSelectorOpen)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-white/10 hover:bg-white/15 transition-colors text-left"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">
                  {selectedWarehouse?.name || 'Select warehouse'}
                </p>
                {selectedWarehouse?.address && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <LocationOnIcon className="text-white/40 flex-shrink-0" sx={{ fontSize: 11 }} />
                    <p className="text-[11px] text-white/50 truncate">
                      {selectedWarehouse.address}
                    </p>
                  </div>
                )}
              </div>
              <UnfoldMoreIcon className="text-white/50 flex-shrink-0 ml-1" sx={{ fontSize: 16 }} />
            </button>

            {/* Dropdown */}
            {isSelectorOpen && (
              <div className="mt-1 rounded-md bg-white dark:bg-gray-700 shadow-lg ring-1 ring-black/5 max-h-48 overflow-y-auto z-50">
                {warehouses
                  .filter((w) => w.isActive)
                  .map((wh) => (
                  <button type="button"
                    key={wh.warehouseId}
                    onClick={() => handleSelectWarehouse(wh)}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                      selectedWarehouse?.warehouseId === wh.warehouseId ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{wh.name}</p>
                      {wh.isDefault && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded flex-shrink-0">
                          Default
                        </span>
                      )}
                    </div>
                    {wh.address && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{wh.address}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation Items */}
        <div className="flex w-full flex-col items-start mt-2">
          {visibleNavItems.map((item) => (
            <SidebarNavButton
              key={item.id}
              label={item.name}
              link={item.disabled ? undefined : item.path}
              iconLeft={item.icon}
              active={activeItemId === item.id}
              iconRight={
                item.badge ? (
                  <span className="bg-yellow-500 text-yellow-900 text-xs font-medium px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                ) : undefined
              }
              className={`
                ${activeItemId === item.id ? 'border-r-2 border-orange-400 dark:border-orange-300' : ''}
                ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            />
          ))}
        </div>
      </div>

      {/* Page Content */}
      {children}
    </>
  );
};

export const WarehouseLayout: React.FC<WarehouseLayoutProps> = ({ children }) => {
  return (
    <WarehouseProvider>
      <WarehouseLayoutInner>{children}</WarehouseLayoutInner>
    </WarehouseProvider>
  );
};
